import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {LazyMasonryGrid} from '@Components/lazy-masonry-grid';
import type {StockButtonFilters, StockPrimaryCTA} from '@Panels/stock-panel/stock-panel.types';
import {StockGridIDs} from '@Panels/stock-panel/stock-panel.types';
import {getUniqueString} from '@Utils/string.util';
import type {LazyLoadingResponse, StockBackendData, StockGetterParams, StockOffsets, StockResponse} from '@Libraries/pmw-stock-media-library';
import {getStockLibraryAudioCategories, StockSources} from '@Libraries/pmw-stock-media-library';
import type {GridItemStorage, GridStockMediaItemBackendData} from '@Components/base-grid/components/grid-item';
import {
  animatedStickerDataToAnimatedStickerItem,
  audioDataToAudioItem,
  clipartDataToClipartItem,
  extractedGettyStickerDataToStickerItem,
  iconDataToIconItem,
  imageDataToImageItem,
  isImageSource,
  shapeDataToShapeItem,
  stillStickerDataToStickerItem,
  videoDataToVideoItem,
} from '@Panels/stock-panel/stock-panel.library';
import type {GridStockImageItemBackEndData} from '@Components/base-grid/components/grid-stock-image-item';
import type {GridStockVideoItemBackEndData} from '@Components/base-grid/components/grid-stock-video-item';
import type {GridClipartItemBackEndData} from '@Components/base-grid/components/grid-clipart-item';
import StockPanelError from '@Panels/stock-panel/components/stock-panel-error';
import {clearSelectedItems, getNoOfGridItems, getSelectedGridItems, reloadGrid, toggleGridItemSelection} from '@Components/base-grid';
import StockPanelNoResults from '@Panels/stock-panel/components/stock-panel-no-results';
import type {PMWStockBackendAnimatedSticker, PMWStockBackendShape} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import type {GridIconItemBackEndData} from '@Components/base-grid/components/grid-icon-item';
import type {GridStillStickerItemBackEndData} from '@Components/base-grid/components/grid-still-sticker-item';
import type {Dimensions} from '@floating-ui/react';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import {ElementTestId} from '@Tests/tests.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {noop} from '@Utils/general.util';

export interface StockPanelGridProps {
  maxSelectionNumber?: number;
  gridID: string;
  sources: StockSources[];
  className?: string;
  selectionGroupID?: string;
  searchTerm?: string;
  onSingleItemDoubleClick?: StockPrimaryCTA;
  generateImageCallback?(): void;
  gridItemDimensions?: Dimensions;
  isManageappPage?: boolean;
}

export default function StockPanelGrid({...props}: StockPanelGridProps): ReactElement<any> {
  const selectionGroupID = useRef(props.selectionGroupID ?? getUniqueString());
  const [noResults, setNoResults] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const dispatch = useAppDispatch();
  const currentResults = useAppSelector((state) => {
    return getNoOfGridItems(state, props.gridID);
  });
  const selectedItems = useAppSelector((state) => {
    return getSelectedGridItems(state, selectionGroupID.current);
  });
  const stockGetterOptions = useAppSelector((state) => {
    return state.stockPanel.stockGetterOptions;
  });
  const abortControllerRef = useRef<AbortController | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const audioCategories = useRef<Record<string, string>>({});

  const onGridItemClicked = (item: GridItemStorage, isDoubleClick: boolean): void => {
    if (isDoubleClick && selectedItems.length === 1 && props.onSingleItemDoubleClick) {
      void props.onSingleItemDoubleClick(selectedItems);
    } else {
      dispatch(
        toggleGridItemSelection({
          gridItemId: item.id,
          gridSelectionGroupId: selectionGroupID.current,
        })
      );
    }
  };

  const getGrid = (): ReactElement<any> => {
    const gridProps = {
      getItems,
      customOnGridItemClicked: onGridItemClicked,
      className: `spacing-p-t-6 spacing-p-b-6 spacing-p-r-3 ${props.className ?? ''}`,
      gridSelectionGroupId: selectionGroupID.current,
      maxSelectionNumber: props.maxSelectionNumber,
      clearGridOnUnmount: true,
      gridId: props.gridID,
      customDimensions: props.gridItemDimensions,
    };

    return (
      <LazyMasonryGrid
        key={props.gridID}
        {...gridProps}
        gridRef={(ref) => {
          gridRef.current = ref;
        }}
        data-testid={ElementTestId.STOCK_PANE_LAZY_GRID}
      />
    );
  };

  const getAdditionalSearchParams = (): string => {
    return `${stockGetterOptions.seasonalSearch ?? ''} ${stockGetterOptions.shapesCategory ?? ''}`;
  }

  const fetchStockMedia = async (getterParams: StockGetterParams): Promise<StockResponse> => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;

    const offsetsToRequest: StockOffsets | Record<string, number> = {};

    props.sources.forEach((source) => {
      offsetsToRequest[source] = getterParams.offsets[source] ?? 0;
    });

    const requestData = {
      limit: getterParams.limit,
      offsets: offsetsToRequest,
      searchTerm: props.searchTerm,
      options: stockGetterOptions,
      isManageappPage: props.isManageappPage ? '1' : '0',
    };

    const queryParams = new URLSearchParams();
    queryParams.append('limit', `${requestData.limit}`);
    queryParams.append('searchTerm', `${requestData.searchTerm} ${getAdditionalSearchParams()}`);
    queryParams.append('isManageappPage', requestData.isManageappPage);
    queryParams.append('options', JSON.stringify(requestData.options));
    queryParams.append('offsets', JSON.stringify(requestData.offsets));

    const url = `${window.PMW.util.site_url('stockmedia/getStock')}?${queryParams.toString()}`;

    if (requestData.options.audioFilters) {
      Object.keys(requestData.options.audioFilters).forEach((id) => {
        if (requestData.options.audioFilters) {
          requestData.options.audioFilters[id as StockButtonFilters].forEach((idSubfilter) => {
            const name = audioCategories.current[idSubfilter];
            trackPosterBuilderGA4Events(GA4EventName.MediaModal_AudioFilters, {[GA4EventParamName.VALUE]: name});
          });
        }
      });
    }

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Cache-Control': 'max-age=600',
        'X-Requested-With': 'XMLHttpRequest',
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = (await response.json()) as AjaxSuccessResponse;
    return data.data as StockResponse;
  };

  const getItems = async (getterParams: StockGetterParams): Promise<LazyLoadingResponse> => {
    if (props.sources.length <= 0) {
      setNoResults(true);
      return {response: [], offsetsData: {}};
    }

    const offsetsToRequest: StockOffsets | Record<string, number> = {};

    props.sources.forEach((source) => {
      offsetsToRequest[source] = getterParams.offsets[source] ?? 0;
    });

    let stock: StockBackendData[];
    let offsets: StockOffsets;

    try {
      const responseFromBackend = await fetchStockMedia(getterParams);
      stock = responseFromBackend.stock;
      offsets = responseFromBackend.offsets;
      setErrorState(false);
    } catch (e: any) {
      if (e.name === 'AbortError') {
        return;
      }
      console.error(e);
      setErrorState(true);
      return {response: [], offsetsData: {}};
    }

    Object.keys(offsets).forEach((key: string) => {
      if (offsets[key as StockSources] === 0) {
        delete offsets[key as StockSources];
      }
    });
    const response: GridItemStorage[] = [];

    const totalResults = stock.length;

    if (totalResults <= 0 && currentResults <= 0) {
      setNoResults(true);
      return {response: [], offsetsData: {}};
    }

    setNoResults(false);

    stock.forEach((stockItemData): void => {
      switch (stockItemData.type as StockSources) {
        case StockSources.PMW_ANIMATED_STICKER:
          response.push(animatedStickerDataToAnimatedStickerItem(stockItemData as PMWStockBackendAnimatedSticker));
          break;
        case StockSources.PMW_SHAPE:
          response.push(shapeDataToShapeItem(stockItemData as PMWStockBackendShape));
          break;
        case StockSources.GETTY_IMAGE:
        case StockSources.STORYBLOCK_IMAGE:
        case StockSources.PIXABY_IMAGE:
        case StockSources.PMW_STOCK_IMAGE:
          response.push(imageDataToImageItem(stockItemData as GridStockImageItemBackEndData, stockItemData.type as StockSources));
          break;
        case StockSources.GETTY_VIDEO:
        case StockSources.STORYBLOCK_VIDEO:
        case StockSources.PIXABY_VIDEO: {
          const videoGridItem = videoDataToVideoItem(stockItemData as GridStockVideoItemBackEndData, stockItemData.type as StockSources);
          if (videoGridItem) {
            response.push(videoGridItem);
          }
          break;
        }
        case StockSources.STORYBLOCK_ILLUSTRATION:
        case StockSources.PIXABY_ILLUSTRATION:
        case StockSources.GETTY_ILLUSTRATION:
          response.push(clipartDataToClipartItem(stockItemData as unknown as GridClipartItemBackEndData, stockItemData.type as StockSources));
          break;
        case StockSources.STORYBLOCK_AUDIO:
          response.push(audioDataToAudioItem(stockItemData));
          break;
        case StockSources.PMW_ICON:
          response.push(iconDataToIconItem(stockItemData as unknown as GridIconItemBackEndData));
          break;
        case StockSources.PMW_EXTRACTED_GETTY_STICKER:
          response.push(extractedGettyStickerDataToStickerItem(stockItemData as unknown as GridStockMediaItemBackendData));
          break;
        case StockSources.PMW_STILL_STICKER:
          response.push(stillStickerDataToStickerItem(stockItemData as unknown as GridStillStickerItemBackEndData));
          break;
        default:
          throw new Error(`Unhandled stock source: ${stockItemData.type ?? ''}`);
      }
    });

    return {response, offsetsData: offsets};
  };

  const getNoResultsPanel = (): ReactElement<any> => {
    if (props.sources.length > 0 && props.sources.every(isImageSource)) {
      return (
        <StockPanelNoResults
          generationIcon="icon-ai-image"
          generationText={window.i18next.t('pmwjs_generate_ai_image_for_search_term', {
            searchTerm: props.searchTerm,
          })}
          onGenerationTextClicked={() => {
            if (props.generateImageCallback) {
              props.generateImageCallback();
            }
          }}
          searchTerm={props.searchTerm ?? ''}
        />
      );
    }
    return <StockPanelNoResults searchTerm={props.searchTerm ?? ''} />;
  };

  useEffect(() => {
    setNoResults(false);
    if ((props.searchTerm !== '' || typeof props.searchTerm !== 'undefined') && props.gridID !== StockGridIDs.TEXT_TO_IMAGE) {
      dispatch(
        reloadGrid({
          gridId: props.gridID,
          gridSelectionGroupId: selectionGroupID.current,
        })
      );

      dispatch(
        clearSelectedItems({
          gridSelectionGroupId: selectionGroupID.current,
        })
      );
    }

    if (gridRef.current) {
      gridRef.current.scrollTo({top: 0, behavior: 'smooth'});
    }

    return (): void => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [props.searchTerm, props.sources.length, stockGetterOptions]);

  const init = async (): Promise<void> => {
    const response = await getStockLibraryAudioCategories();
    audioCategories.current = response
      .flatMap((category) => category.subcategories)
      .reduce<Record<string, string>>((acc, subcategory) => {
        acc[subcategory.id.toString()] = subcategory.displayName;
        return acc;
      }, {});
  };

  useEffect(() => {
    void init();
    return noop;
  }, []);

  if (errorState) {
    return (
      <StockPanelError
        className="_full-height _full-width"
        text={window.i18next.t('pmwjs_refresh_on_error')}
        onRefresh={(): void => {
          dispatch(
            reloadGrid({
              gridId: props.gridID,
              gridSelectionGroupId: selectionGroupID.current,
            })
          );
          setErrorState(false);
        }}
      />
    );
  }

  return noResults && (props.searchTerm !== '' || typeof props.searchTerm !== 'undefined') ? getNoResultsPanel() : getGrid();
}
